import { useDateTimeFormatterV2 } from '@beeline.com/bp-design-system';
import { useExportNotificationsIntlModule } from 'common/header/notifications/hooks/mappers/intl/export/useExportNotificationsIntl';
import type { MappedNotification, NotificationMapperOutput } from 'common/header/notifications/Notifications.types';
import { useFormatNotificationMessage, useNotificationsIntl } from 'common/header/translations/useNotificationsIntl';
import { useMemo } from 'react';
import { useNumberFormatter } from 'src/utils/useNumberFormatter';
import { useNotifications } from 'utils/hooks/data/useNotifications';
import { useTenantCode } from 'utils/hooks/useTenantConfig';
import { mapNotifications } from './mappers';

export const useNotificationMapperV2 = (): NotificationMapperOutput => {
  const {
    notifications,
    isFullyLoaded,
    error,
    markNotificationsAsRead,
    deleteNotifications,
    refetchNotifications,
    loading,
    loadMore
  } = useNotifications();
  const tenantCode = useTenantCode();
  const formatNumber = useNumberFormatter();
  const formatNotificationMessage = useFormatNotificationMessage();
  const intl = useNotificationsIntl();
  const formatExportNotificationMessage = useExportNotificationsIntlModule();
  const formatDateTime = useDateTimeFormatterV2();

  const mappedNotifications = useMemo(
    (): MappedNotification[] =>
      mapNotifications(notifications, {
        formatNumber,
        formatNotificationMessage,
        formatExportNotificationMessage,
        formatDateTime,
        intl,
        tenantCode,
        flags: {
          noInitialTotalCount: true
        }
      }),
    [
      formatNumber,
      formatNotificationMessage,
      formatDateTime,
      formatExportNotificationMessage,
      intl,
      tenantCode,
      notifications
    ]
  );

  const unReadCount = useMemo(
    () => Math.min(mappedNotifications.filter((el) => !el.read).length, 100),
    [mappedNotifications]
  );

  return {
    notifications: mappedNotifications,
    isFullyLoaded,
    error,
    unReadCount,
    markNotificationsAsRead,
    deleteNotifications,
    refetchNotifications,
    loadMore,
    loading
  };
};
